import { loadFlowGuideSteps, toBodyHtml, type GuideStep } from "../loadProjectOverviewGuideSteps";
import { projectExistingUserSteps } from "../steps/projectExistingUserSteps";
import { projectNewUserSteps } from "../steps/projectNewUserSteps";
import { PROJECT_WELCOME_FLOW_KEY } from "../welcomeFlow.config";
import { replayGuideStep } from "./replayGuideStep";
import type { WelcomeDeckWithFlow } from "./WelcomeDeck";

// Renamed from `sub-sidebar-config-nav-link` to match the Carbon0 row's "Settings" label.
const SETTINGS_ANCHOR_ID = "sub-sidebar-settings-nav-link";

// Not a Carbon0 row — see Filter.vue for the anchor and WelcomeTour.vue's markStepSeen for the
// empty-id skip that keeps this step from trying to mark itself seen server-side.
const MODEL_SELECT_ANCHOR_ID = "model-select";
const FILTERS_STEP: GuideStep = {
  id: "",
  key: "project-global-filters",
  anchorId: "project-global-filters",
  title: "Project filters",
  bodyHtml: toBodyHtml(
    "Models, life-cycle stages, indicator and unit are set once for the whole project, right here. The dashboard and every mapping page follow what you pick."
  ),
  alreadySeen: false,
  highlight: true,
};

// The rest of the filter bar, walked right after Project Models — one local step per remaining
// FilterButton (Filter.vue), in the order they sit in the toolbar.
const LOCAL_FILTER_STEPS: GuideStep[] = [
  {
    id: "",
    key: "stage-select",
    anchorId: "stage-select",
    title: "Life-cycle stages",
    bodyHtml: toBodyHtml(
      "Choose which life-cycle stages count in the figures, from production to end of life. Turn one off and every number on every page leaves it out."
    ),
    alreadySeen: false,
    highlight: true,
  },
  {
    id: "",
    key: "indicator-select",
    anchorId: "indicator-select",
    title: "Indicator",
    bodyHtml: toBodyHtml(
      "Pick the environmental indicator every figure is shown in. Global warming potential (GWP) is the default."
    ),
    alreadySeen: false,
    highlight: true,
  },
  {
    id: "",
    key: "unit-select",
    anchorId: "unit-select",
    title: "Units",
    bodyHtml: toBodyHtml("Choose the unit every figure is shown in, for example total emissions or per square metre per year."),
    alreadySeen: false,
    highlight: true,
  },
];

// FILTERS_STEP sits right before Project Models, LOCAL_FILTER_STEPS right after — both collapse onto
// FILTERS_STEP followed by LOCAL_FILTER_STEPS when there's no Project Models row to sit around.
async function loadProjectDeckSteps(): Promise<GuideStep[]> {
  const steps = await loadFlowGuideSteps("Project Dashboard");
  const modelSelectIndex = steps.findIndex((step) => step.anchorId === MODEL_SELECT_ANCHOR_ID);
  if (modelSelectIndex === -1) {
    return [...steps, FILTERS_STEP, ...LOCAL_FILTER_STEPS, replayGuideStep("sub-sidebar-dashboard-nav-link")];
  }
  return [
    ...steps.slice(0, modelSelectIndex),
    FILTERS_STEP,
    ...steps.slice(modelSelectIndex, modelSelectIndex + 1),
    ...LOCAL_FILTER_STEPS,
    ...steps.slice(modelSelectIndex + 1),
    replayGuideStep("sub-sidebar-dashboard-nav-link"),
  ];
}

export const projectDeck: WelcomeDeckWithFlow = {
  id: "project",
  flowKey: PROJECT_WELCOME_FLOW_KEY,
  intro: {
    new: projectNewUserSteps[0],
    existing: projectExistingUserSteps[0],
  },
  loadSteps: loadProjectDeckSteps,
  ending: {
    anchorId: SETTINGS_ANCHOR_ID,
    // Only the run that just created this project is handed off to settings.
    ctaLabel: (context) => (context.afterCreation ? "Set up your project" : undefined),
    runCta: async (context) => {
      if (!context.projectId) return;
      await context.router.push({ name: "projectSettings", params: { id: context.projectId } });
    },
  },
};
